import type { ErrorInfo, ReactNode } from 'react';
import { Component } from 'react';
import { ErrorView } from './ErrorView';
import { logError } from '@/utils/log';

interface AppErrorBoundaryProps {
  children: ReactNode;
}

interface AppErrorBoundaryState {
  hasError: boolean;
  errorId?: string;
}

export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = {
    hasError: false,
    errorId: undefined,
  };

  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    const errorId = logError('boundary', error, {
      componentStack: info.componentStack,
    });

    this.setState({ errorId });
  }

  private handleReload = () => {
    window.location.reload();
  };

  override render() {
    if (this.state.hasError) {
      // Outside the router and the settings provider (see main.tsx), so standalone.
      return (
        <ErrorView
          standalone
          eyebrow="Application Error"
          title="Well. That broke."
          message="Something went sideways while rendering this page. I logged the failure, kept the details out of the UI, and left a recovery path instead of a white screen."
          errorId={this.state.errorId}
          primaryAction={{ label: 'Reload page', onClick: this.handleReload }}
          secondaryAction={{ label: 'Return home', to: '/' }}
          supportLink={{ to: '/suggest', label: 'Leave a note in Suggestion Box' }}
        />
      );
    }

    return this.props.children;
  }
}
